import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format, addHours, parse, isValid } from 'date-fns';
import Papa from 'papaparse';
import toast from 'react-hot-toast';
import {
    useTheme,
    Box,
    Tooltip,
    TextField,
    MenuItem,
    Divider,
    alpha,
} from '@mui/material';
import {
    Add, 
    DeleteOutline, 
    DeleteForever,
    ContentCut, 
    ContentCopy, 
    ContentPaste,
    FileUpload, 
    SaveAlt, 
} from '@mui/icons-material';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import ScrollToTop from 'components/ScrollToTop';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { getSelectedIds } from 'state/helpers';


// ─── CONSTANTS ───────────────────────────────────────────────────────────────

const REQUIRED_FIELDS = ['flightNo', 'dep', 'arr', 'std', 'sta', 'fromDate', 'toDate'];

// TSV column order for clipboard paste/copy
const TSV_COLUMNS = ['acType', 'fromDate', 'toDate', 'baseOut', 'baseIn', 'dep', 'arr', 'sector', 'flightNo', 'std', 'sta', 'blockTime', 'blockTimePlus', 'tat', 'out', 'in', 'frequency', 'flightType'];

let _idCounter = 0;
const nextId = () => ++_idCounter;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/** Parse delimited text (clipboard paste from Excel/spreadsheet) → rows via PapaParse.
 *  Column order: flightNo dep arr fromDate toDate std sta acType frequency flightType
 */
const parseTsv = (text) => {
    const { data } = Papa.parse(text.trim(), { delimiter: '\t', skipEmptyLines: true });
    return data
        .map((cells) => {
            const row = { _id: nextId() };
            TSV_COLUMNS.forEach((col, i) => { row[col] = String(cells[i] ?? '').trim(); });
            return row;
        })
        .filter((r) => TSV_COLUMNS.some((c) => r[c]));
};

/** Parse a date string in either ISO ("2025-01-01") or SSIM ("01JAN25") format */
const parseRowDate = (str) => {
    if (!str) return null;
    const iso = new Date(str);
    if (isValid(iso)) return iso;
    const ssim = parse(str, 'ddMMMyy', new Date());
    return isValid(ssim) ? ssim : null;
};

/** Serialize rows to TSV via PapaParse */
const rowsToTsv = (rows) =>
    Papa.unparse(
        rows.map((r) => TSV_COLUMNS.map((c) => r[c] ?? '')),
        { delimiter: '\t', newline: '\n' },
    );

// ─── COMPONENT ───────────────────────────────────────────────────────────────

const SSIM = () => {
    useAuthCheck();
    const theme = useTheme();
    const params = useSelector((state) => state.params);
    const CARRIER_CODE = params?.find((p) => p.param === 'IATA_AIRLINE_CODE')?.value || '';

    const [rows, setRows] = useState([]);
    const [selection, setSelection] = useState({ type: 'include', ids: new Set() });
    const [carrier, setCarrier] = useState(CARRIER_CODE);
    const [timeMode, setTimeMode] = useState('Local');
    const [airportData, setAirportData] = useState([]);
    const [fileName, setFileName] = useState('');


    // ── Derived ──────────────────────────────────────────────────────────────

    const selectedIds = useMemo(() => getSelectedIds(selection, rows), [selection, rows]);
    const hasSelection = selectedIds.length > 0;

    const exportDisabledReason = useMemo(() => {
        if (rows.length === 0) return 'No rows to export';
        if (timeMode === 'Local' && airportData.length === 0) return 'Upload timezone file to export in Local mode';
        return null;
    }, [rows.length, timeMode, airportData.length]);


    // ── Row operations ────────────────────────────────────────────────────────

    const handleRowUpdate = useCallback((newRow) => {
        setRows((prev) => prev.map((r) => (r._id === newRow._id ? { ...r, ...newRow } : r)));
        return newRow;
    }, []);

    const handleAddRow = useCallback(() => {
        setRows((prev) => [...prev, {
            _id: nextId(), acType: '', fromDate: '', toDate: '',
            baseOut: '', baseIn: '', dep: '', arr: '', sector: '',
            flightNo: '', std: '', sta: '', blockTime: '', blockTimePlus: '',
            tat: '', out: '', in: '', frequency: '1234567', flightType: 'J',
        }]);
    }, []);

    const handleDeleteRows = useCallback(() => {
        if (!hasSelection) { toast.error('No rows selected.'); return; }
        setRows((prev) => prev.filter((r) => !selectedIds.includes(r._id)));
        setSelection({ type: 'include', ids: new Set() });
        toast.success(`${selectedIds.length} row(s) removed.`);
    }, [hasSelection, selectedIds]);

    const handleClearAll = useCallback(() => {
        if (rows.length === 0) return;
        setRows([]);
        setSelection({ type: 'include', ids: new Set() });
        toast.success('All rows cleared.');
    }, [rows.length]);

    // ── Clipboard ─────────────────────────────────────────────────────────────

    const handleCopy = useCallback(async () => {
        if (!hasSelection) { toast.error('Select rows to copy.'); return; }
        const selected = rows.filter((r) => selectedIds.includes(r._id));
        const tsv = rowsToTsv(selected);
        try {
            await navigator.clipboard.writeText(tsv);
            toast.success(`${selected.length} row(s) copied.`);
        } catch {
            toast.error('Clipboard access denied.');
        }
    }, [hasSelection, rows, selectedIds]);

    const handleCut = useCallback(async () => {
        if (!hasSelection) { toast.error('Select rows to cut.'); return; }
        const selected = rows.filter((r) => selectedIds.includes(r._id));
        const tsv = rowsToTsv(selected);
        try {
            await navigator.clipboard.writeText(tsv);
            setRows((prev) => prev.filter((r) => !selectedIds.includes(r._id)));
            setSelection({ type: 'include', ids: new Set() });
            toast.success(`${selected.length} row(s) cut.`);
        } catch {
            toast.error('Clipboard access denied!');
        }
    }, [hasSelection, rows, selectedIds]);

    const handlePasteFromClipboard = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (!text.trim()) { toast.error('Clipboard is empty.'); return; }
            const parsed = parseTsv(text);
            if (!parsed.length) { toast.error('No valid rows in clipboard.'); return; }
            setRows((prev) => [...prev, ...parsed]);
            toast.success(`${parsed.length} row(s) pasted.`);
        } catch {
            toast.error('Clipboard access denied - use Ctrl+V in the grid instead.');
        }
    }, []);

    // Keyboard handler on grid wrapper
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Delete' && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            handleDeleteRows();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
            e.preventDefault();
            handleAddRow();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
            handleCopy();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
            e.preventDefault();
            handleCut();
        }
    }, [handleDeleteRows, handleAddRow, handleCopy, handleCut]);

    // In-grid paste (Ctrl+V while grid is focused)
    const handleGridPaste = useCallback((e) => {
        // Don't intercept if a cell is being edited
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        const text = e.clipboardData?.getData('text');
        if (!text?.trim()) return;
        const parsed = parseTsv(text);
        if (!parsed.length) { toast.error('No valid rows to paste.'); return; }
        setRows((prev) => [...prev, ...parsed]);
        toast.success(`${parsed.length} row(s) pasted.`);
    }, []);

    
    const handleUpload = (event) => {
        const files = event.target.files;
        if (!files.length) return;

        const file = files[0];
        if (!file.name.endsWith('.txt')) {
            toast.error('Please upload a .txt file.');
            return;
        }

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const fileContent = e.target.result;
                const fileRows = fileContent.split('\n').map((row) => row.split(','));

                const formattedData = fileRows
                    .map((row) => {
                        if (row.length !== 14) return null;
                        return {
                            no:          row[0]?.replace(/"/g, '').replace(/\r/g, ''),
                            airportName: row[1]?.replace(/"/g, '').replace(/\r/g, ''),
                            city:        row[2]?.replace(/"/g, '').replace(/\r/g, ''),
                            country:     row[3]?.replace(/"/g, '').replace(/\r/g, ''),
                            IATA:        row[4]?.replace(/"/g, '').replace(/\r/g, ''),
                            ICAO:        row[5]?.replace(/"/g, '').replace(/\r/g, ''),
                            latitude:    row[6]?.replace(/"/g, '').replace(/\r/g, ''),
                            longitude:   row[7]?.replace(/"/g, '').replace(/\r/g, ''),
                            altitude:    row[8]?.replace(/"/g, '').replace(/\r/g, ''),
                            timezone:    row[9]?.replace(/"/g, '').replace(/\r/g, ''),
                            DST:         row[10]?.replace(/"/g, '').replace(/\r/g, ''),
                            tzArea:      row[11]?.replace(/"/g, '').replace(/\r/g, ''),
                            type:        row[12]?.replace(/"/g, '').replace(/\r/g, ''),
                            source:      row[13]?.replace(/"/g, '').replace(/\r/g, ''),
                        };
                    })
                    .filter(Boolean);

                setAirportData(formattedData);
                toast.success('File uploaded successfully!');
            } catch (err) {
                toast.error(err.message || 'Failed to parse file!');
            }
        };
        reader.onerror = () => toast.error('File reading error!');
        reader.readAsText(file);
    };

    const handleExport = () => {
        try {
            if (rows.length === 0) {
                toast.error("No data to export!");
                return;
            }
    
            const timeModeIndicator = timeMode === "UTC" ? "U" : "L";

            const parsedFromDates = rows.map((r) => parseRowDate(r.fromDate)).filter(Boolean).sort((a, b) => a - b);
            const parsedToDates = rows.map((r) => parseRowDate(r.toDate)).filter(Boolean).sort((a, b) => a - b);
            const startDate = parsedFromDates[0] ?? new Date();
            const endDate = parsedToDates[parsedToDates.length - 1] ?? new Date();

            let ssimContent = '';
            const startTitle = '1AIRLINE STANDARD SCHEDULE DATA SET';
            const endTitle = '001000001';
            ssimContent += startTitle + ' '.repeat(200 - startTitle.length - endTitle.length) + endTitle;

            for (let i = 0; i < 4; i++) {
                ssimContent += '\r\n' + '0'.repeat(200);
            }

            const endSecondRow = rows.length + '000002';

            ssimContent += '\r\n';
            ssimContent += `2${timeModeIndicator}${carrier}` +
                           `${' '.repeat(10)}` +
                           `${format(startDate, "ddMMMyy").toUpperCase()}` +
                           `${format(endDate, "ddMMMyy").toUpperCase()}` +
                           `${format(new Date(), "ddMMMyy").toUpperCase()}` + 
                           `${' '.repeat(36)}` + 
                           `P` + 
                           `${' '.repeat(128 - endSecondRow.length)}` + 
                           `${endSecondRow}`;
               
            for (let i = 0; i < 4; i++) {
                ssimContent += '\r\n' + '0'.repeat(200);  
            };
        
            const generateDow = (frequency) => {
                const days = Array(7).fill(' ');
                for (const char of frequency) {
                    const index = parseInt(char, 10) - 1;
                    if (index >= 0 && index < 7) {
                        days[index] = char;
                    }
                }
                return days.join('');
            };
    
            const formatTimezone = (timezone) => {
                const sign = Number(timezone) >= 0 ? '+' : '-';
                const absoluteTimezone = Math.abs(timezone);
                const hours = Math.floor(absoluteTimezone).toString().padStart(2, '0'); // Get the hours
                const minutes = Math.round((absoluteTimezone - Math.floor(absoluteTimezone)) * 60).toString().padStart(2, '0'); // Get the minutes
                
                return `${sign}${hours}${minutes}`;
            };
    
            const adjustTimeToUTC = (time, offset) => {
                // Convert the time to a Date object
                const hours = parseInt(time.slice(0, 2), 10);
                const minutes = parseInt(time.slice(2, 4), 10);
            
                const timeDate = new Date(2020, 0, 1, hours, minutes); 
            
                // Adjust the time by subtracting the offset (hours)
                const adjustedTime = addHours(timeDate, -offset);
            
                // Return the adjusted time in 'HHmm' format
                return format(adjustedTime, 'HHmm');
            };
    
            rows.forEach((row, index) => {
                if (!row.fromDate || !row.toDate || !row.dep || !row.arr || !row.std || !row.sta) {
                    return;
                }           
    
                const fromDateObj = parseRowDate(row.fromDate);
                const toDateObj = parseRowDate(row.toDate);
                if (!fromDateObj || !toDateObj) return;
                const fromDate = format(fromDateObj, 'ddMMMyy').toUpperCase();
                const toDate = format(toDateObj, 'ddMMMyy').toUpperCase();
    
                const flightType = row.flightType?.trim() || 'J';
                const dow = generateDow(row.frequency);
    
                let std = row.std.replace(':', '').padStart(4, '0');
                let sta = row.sta.replace(':', '').padStart(4, '0');
    
                const depTZ = airportData?.find((airport) => airport.IATA === row.dep)?.timezone;
                const arrTZ = airportData?.find((airport) => airport.IATA === row.arr)?.timezone;
                const formattedDepTZ = depTZ ? formatTimezone(depTZ) : '+0700';
                const formattedArrTZ = arrTZ ? formatTimezone(arrTZ) : '+0700';
    
                if (timeMode === 'UTC' && depTZ) {
                    std = adjustTimeToUTC(std, Number(depTZ));
                    sta = adjustTimeToUTC(sta, Number(arrTZ));
                }
    
                let onwardFlight = '';
                if (rows[index]?.acType.trim() === rows[index + 1]?.acType.trim()
                    && rows[index]?.arr.trim() === rows[index + 1]?.dep.trim()) {
                        onwardFlight = rows[index + 1]?.flightNo;
                } else {                
                    const firstMatchingFlight = rows.find(
                        (row, idx) => idx !== index && row.acType.trim() === rows[index]?.acType.trim() && row.dep.trim() === rows[index]?.arr.trim()
                    );
                    onwardFlight = firstMatchingFlight?.flightNo || '';
                }
                
                ssimContent += `\r\n` +
                               `3${carrier.trim().padStart(3)}` + 
                               `${row.flightNo.trim().replace(carrier, '').padStart(5)}` + 
                               `0101${flightType}` +
                               `${fromDate.trim()}${toDate.trim()}` + 
                               `${dow} ` + 
                               `${row.dep.trim()}${std}${std}${formattedDepTZ}  ` + 
                               `${row.arr.trim()}${sta}${sta}${formattedArrTZ}` +
                               `${row.acType.trim().slice(-3).padStart(5)}` +
                               `${' '.repeat(62)}` +
                               `${carrier.trim()}` +
                               `${onwardFlight.trim().replace(carrier, '').padStart(5)}` +
                               `${' '.repeat(28)}` +
                               `VV` +
                               `${row.acType.trim().slice(-3)}` +
                               `${' '.repeat(17)}` +
                               `${(index + 3).toString().padStart(6, '0')}`;
            });
    
            const endLastRow = (rows.length + 2).toString().padStart(6, '0') + 'E' + (rows.length + 3).toString().padStart(6, '0');
    
            ssimContent += '\r\n';
            ssimContent += `5${carrier.padStart(3)}` +
                           `${' '.repeat(196 - endLastRow.length)}` +
                           `${endLastRow}`;
            
            for (let i = 0; i < 14; i++) {
                ssimContent += '\r\n' + '0'.repeat(200);  
            };
            
    
            const blob = new Blob([ssimContent], { type: "text/csv;charset=utf-8" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "ssim_data.txt";
            link.click();
        } catch (error) {
            toast.error(error.message || 'Export failed!');
        }
    };


    // ── Columns ───────────────────────────────────────────────────────────────

    const columns = useMemo(() => [
        { field: 'acType',        headerName: 'ACType',        editable: true, flex: 1.2 },
        { field: 'fromDate',      headerName: 'FromDate',      editable: true, flex: 1.5 },
        { field: 'toDate',        headerName: 'ToDate',        editable: true, flex: 1.5 },
        { field: 'baseOut',       headerName: 'BaseOut',       editable: true, flex: 1   },
        { field: 'baseIn',        headerName: 'BaseIn',        editable: true, flex: 1   },
        { field: 'dep',           headerName: 'Dep',           editable: true, flex: 1   },
        { field: 'arr',           headerName: 'Arr',           editable: true, flex: 1   },
        { field: 'sector',        headerName: 'Sector',        editable: true, flex: 1.5 },
        { field: 'flightNo',      headerName: 'FlightNo',      editable: true, flex: 1.2 },
        { field: 'std',           headerName: 'Std',           editable: true, flex: 1   },
        { field: 'sta',           headerName: 'Sta',           editable: true, flex: 1   },
        { field: 'blockTime',     headerName: 'BlockTime',     editable: true, flex: 1.2 },
        { field: 'blockTimePlus', headerName: 'BlockTimePlus', editable: true, flex: 1.2 },
        { field: 'tat',           headerName: 'TAT',           editable: true, flex: 1   },
        { field: 'out',           headerName: 'Out',           editable: true, flex: 0.5 },
        { field: 'in',            headerName: 'In',            editable: true, flex: 0.5 },
        { field: 'frequency',     headerName: 'Frequency',     editable: true, flex: 1.3 },
        { field: 'flightType',    headerName: 'FlightType',    editable: true, flex: 1.3 },
    ], []);

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Box className="main-container-box">
            {/* ── Header + Toolbar ─────────────────────────────────────────── */}
            <FlexBetween>
                <Header title="SSIM" subtitle="List of Messages" />

                <Box 
                    display="flex" 
                    alignItems="center" 
                    flexShrink={0}
                    gap={1} 
                >
                    {/* Desktop */}
                    <Box 
                        sx={{ 
                            display: { xs: 'none', sm: 'flex' }, 
                            alignItems: 'center', 
                            gap: 1, 
                        }}
                    >
                        <Tooltip title="Insert (Ctrl+N)" arrow>
                            <span>
                                <StyledButton onClick={handleAddRow}>
                                    <Add />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete (Del)" arrow>
                            <span>
                                <StyledButton onClick={handleDeleteRows} disabled={!hasSelection}>
                                    <DeleteOutline />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Clear all rows" arrow>
                            <span>
                                <StyledButton onClick={handleClearAll} disabled={rows.length === 0}>
                                    <DeleteForever />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Tooltip title="Cut (Ctrl+X)" arrow>
                            <span>
                                <StyledButton onClick={handleCut} disabled={!hasSelection}>
                                    <ContentCut />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Copy (Ctrl+C)" arrow>
                            <span>
                                <StyledButton onClick={handleCopy} disabled={!hasSelection}>
                                    <ContentCopy />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Paste (Ctrl+V)" arrow>
                            <span>
                                <StyledButton onClick={handlePasteFromClipboard}>
                                    <ContentPaste />
                                </StyledButton>
                            </span>
                        </Tooltip>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

                     <Tooltip title="Upload Time Zone file" arrow>
                        <span>
                            <label htmlFor="upload-file">
                                <StyledButton 
                                    component="span"
                                    disabled={!!fileName}
                                >
                                    <FileUpload />
                                </StyledButton>
                            </label>
                            <input
                                id="upload-file"
                                type="file"
                                accept=".txt"
                                style={{ display: 'none' }}
                                onChange={handleUpload}
                            />           
                        </span>
                    </Tooltip>
                    
                    <Tooltip title={exportDisabledReason ?? 'Export SSIM text file'} arrow>
                        <span>
                            <StyledButton
                                component="span"
                                disabled={!!exportDisabledReason}
                                onClick={handleExport}
                            >
                                <SaveAlt />
                            </StyledButton>
                        </span>
                    </Tooltip> 
                    
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

                    {/* Settings */}
                    <TextField
                        select
                        label="Mode"
                        variant="standard"
                        size="small"
                        sx={{ width: 68 }}
                        value={timeMode}
                        onChange={(e) => setTimeMode(e.target.value)}
                    >
                        <MenuItem value="Local">Local</MenuItem>
                        <MenuItem value="UTC">UTC</MenuItem>
                    </TextField>
                    <TextField
                        label="Carrier"
                        variant="standard"
                        size="small"
                        sx={{ width: 56 }}
                        value={carrier}
                        onChange={(e) => setCarrier(e.target.value.toUpperCase().slice(0, 2))}
                        slotProps={{ htmlInput: { maxLength: 2 } }}
                    />
                </Box>
            </FlexBetween>

            {/* ── Grid ─────────────────────────────────────────────────────── */}
            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            >
                <Box
                    height={LAYOUT_HEIGHT}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onPaste={handleGridPaste}
                    sx={{ gridColumn: 'span 12', overflowY: 'auto', outline: 'none' }}
                >
                    <DataGridComponent
                        rows={rows}
                        columns={columns}
                        sx={{
                            '& .ssim-cell-error': {
                                backgroundColor: alpha(theme.palette.error.main, 0.12),
                                color: theme.palette.error.main,
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                                },
                            },
                        }}
                        props={{
                            checkboxSelection: true,
                            disableRowSelectionOnClick: true,
                            processRowUpdate: handleRowUpdate,
                            onRowSelectionModelChange: setSelection,
                            rowSelectionModel: selection,
                            pageSizeOptions: [25, 50, 100, 250],
                            initialState: { pagination: { paginationModel: { page: 0, pageSize: 100 } } },
                            getCellClassName: ({ field, row }) => {
                                if (REQUIRED_FIELDS.includes(field) && !String(row[field] ?? '').trim()) {
                                    return 'ssim-cell-error';
                                }
                                return '';
                            },
                        }}
                    />

                    <ScrollToTop />
                </Box>
            </Box>
        </Box>
    );
};

export default SSIM;
